import invariant from 'tiny-invariant'

import type { OfferPageQuickFilters } from 'contexts/OfferPage/offerPageStateReducer'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import {
  isLeCollectRate,
  isPropertyCollectRate,
  isUnavailableRoomRates,
} from 'lib/offer/propertyTypes'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { reportLog } from 'services/userMonitoring'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import type { ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'

interface PropertyRateInclusionGroups {
  luxPlusInclusions: Array<App.OfferInclusion>
  bonusInclusions: Array<App.OfferInclusion>
  bundleInclusions: Array<App.OfferInclusion>
  inclusions: Array<App.OfferInclusion>
}

export function isPropertyInclusionValidForDuration(
  { minDuration, maxDuration }: App.OfferInclusion,
  duration: number,
): boolean {
  return (
    (minDuration == null || duration >= minDuration) &&
    (maxDuration == null || duration <= maxDuration)
  )
}

/**
 * Flattens the 4 inclusion fields on a Property rate into the same order
 * `PropertyRateInclusions.tsx` renders them in: LuxPlus, then bonus, then bundle, then base
 * inclusions. If that render order ever changes, update it here too.
 */
export function getOrderedPropertyInclusions(groups: PropertyRateInclusionGroups): Array<App.OfferInclusion> {
  return [
    ...groups.luxPlusInclusions,
    ...groups.bonusInclusions,
    ...groups.bundleInclusions,
    ...groups.inclusions,
  ]
}

export type PropertyRoomsMapping = Record<string, App.Property.Room>

export interface RoomPackageOptions {
  roomId: string
  propertyRoom: App.Property.Room
}

export interface RoomRateViews {
  roomId: string
  propertyRoom: App.Property.Room
  availableRooms: number | undefined
  rates: Array<App.EnrichedPropertyRate>
  allRatesQuickFiltered: boolean
  roomFitsCapacity: boolean
  datesAvailable: boolean
  isSelected: boolean
  isHighlighted: boolean
  roomRateUnmatched: boolean
}

const emptyRoomsMappings: [PropertyRoomsMapping, PropertyRoomsMapping, PropertyRoomsMapping] =
  [{}, {}, {}]

function getFirstRateAvailableRooms(rates: ReadonlyArray<App.EnrichedPropertyRate>): number | undefined {
  const availableRooms = rates[0]?.availableRooms
  return typeof availableRooms === 'number' ? availableRooms : undefined
}

export function getRoomsMappings(
  propertyRooms: Record<string, App.Property.Room> | undefined,
  roomMappings: App.PropertyOfferMapping['rooms'] | undefined,
): [PropertyRoomsMapping, PropertyRoomsMapping, PropertyRoomsMapping] {
  if (!(propertyRooms && roomMappings)) return emptyRoomsMappings

  const rooms: PropertyRoomsMapping = {}
  const lpcRooms: PropertyRoomsMapping = {}
  const bedbankRooms: PropertyRoomsMapping = {}
  for (const { roomId, lpcRoomIds, bedbankRoomIds } of Object.values(roomMappings)) {
    const room = propertyRooms[roomId]
    if (!room) continue

    rooms[roomId] = room
    for (const id of lpcRoomIds) {
      lpcRooms[id] = room
    }
    for (const id of bedbankRoomIds) {
      bedbankRooms[id] = room
    }
  }
  return [rooms, lpcRooms, bedbankRooms]
}

export function getPropertyRatePassportOffer(
  rate: App.EnrichedPropertyRate,
  property: App.Property.Property,
): ExperiencesPassportOfferSource {
  return {
    id: rate.service.type === 'lpc' ? rate.service.offerId : rate.service.propertyId,
    type: rate.service.type === 'lpc' ? OFFER_TYPE_ALWAYS_ON : OFFER_TYPE_BED_BANK,
    location: property.location.city ?? property.location.countryName,
    property: {
      name: property.name,
      latitude: property.location.latitude,
      longitude: property.location.longitude,
    },
  }
}

export function filterRoomRates(
  room: App.RawPropertyRoomRates,
  quickFilters: Partial<OfferPageQuickFilters> | undefined,
): App.RawPropertyRoomRates {
  if (isUnavailableRoomRates(room) || !quickFilters) return room

  const breakfastIncluded = !!quickFilters.breakfastIncluded
  const freeCancellation = !!quickFilters.freeCancellation
  const freeUpgrades = !!quickFilters.freeUpgrades
  if (!(breakfastIncluded || freeCancellation || freeUpgrades)) return room

  const filteredRateEntries = Object.entries(room.rates).filter(([_, rawRate]) => {
    // Despite how broad `App.RateBoardCode` may be, _our_ `mapBoardCode` only returns
    // `'roomonly' | 'breakfast' | 'halfboard' | 'fullboard' | 'allinclusive'`, so this is fine.
    if (breakfastIncluded && rawRate.boardCode === 'roomonly') return false
    if (freeCancellation && rawRate.headlineCancellationPolicyType !== 'fullyRefundable') return false
    // The raw rate carries its loyalty package-upgrade target as a rate-id string (or undefined) in
    // `relations.upgradeTargets.package`, so upgrade eligibility is testable here directly.
    if (freeUpgrades && !rawRate.relations.upgradeTargets?.package) return false
    return true
  })
  // Only rates that pass the filter are shown as tiles.
  const displayedRateIds = new Set(filteredRateEntries.map(([rateId]) => rateId))

  // Loyalty upgrades: some rates offer loyalty members a free upgrade to a better rate, referenced
  // by id and shown in an upgrade box on the rate tile. That box resolves the id from this map
  // (`getPropertyRatesByRoom`), so keep each kept rate's target rate here even though targets never
  // match the filter themselves — dropping them would blank the box. Targets stay out of the display
  // lists, so they never render as a tile.
  const filteredRates = Object.fromEntries(filteredRateEntries)
  for (const [, rawRate] of filteredRateEntries) {
    const targetRateId = rawRate.relations.upgradeTargets?.package
    const targetRate = targetRateId ? room.rates[targetRateId] : undefined
    if (targetRateId && targetRate) filteredRates[targetRateId] = targetRate
  }

  const filteredHotelOnlyList = room.lists.hotelOnly.filter((item) => displayedRateIds.has(item.id))
  const filteredHotelWithFlightsList = room.lists.hotelWithFlights.filter((item) => displayedRateIds.has(item.id))

  if (
    // ... either of the original entry lists had rates...
    (room.lists.hotelOnly.length || room.lists.hotelWithFlights.length) &&
    // ... but now neither of the filtered entry lists have rates...
    (!filteredHotelOnlyList.length && !filteredHotelWithFlightsList.length)
  ) {
    // This doesn't gracefully handle cases where only one of the entry lists is empty. We'll fix
    // this later.
    return {
      roomId: room.roomId,
      unavailableReason: 'QUICK_FILTERED_OUT',
    }
  }

  return {
    roomId: room.roomId,
    rates: filteredRates,
    lists: {
      hotelOnly: filteredHotelOnlyList,
      hotelWithFlights: filteredHotelWithFlightsList,
    },
  }
}

export function getRatesByRoom(
  mapping: App.PropertyOfferMapping,
  propertyRooms: PropertyRoomsMapping,
  lpcRooms: PropertyRoomsMapping,
  bedbankRooms: PropertyRoomsMapping,
  ratesByRoom: Array<App.EnrichedPropertyRoomRates>,
  flights: boolean,
  selectedRoomId: string | undefined,
  highlightedRoomId: string | undefined,
  highlightedRateId: string | undefined,
): Array<RoomRateViews> {
  // Map from accommodation service room UUIDs to their default order.
  const defaultOrderByRequiredRoomIds = Object.fromEntries(
    Object.entries(propertyRooms).flatMap(([roomId, propertyRoom]) => {
      if (propertyRoom.shouldHideUnlessHasRates) return []
      return [[roomId, propertyRoom.defaultOrder]]
    }),
  )

  const droppedRoomIds = new Set()
  const combinedRatesByRoom: Array<RoomRateViews> = ratesByRoom.flatMap((roomRates, roomIndex) => {
    const { roomId } = roomRates
    delete defaultOrderByRequiredRoomIds[roomId]
    if (!Object.hasOwn(propertyRooms, roomId)) {
      droppedRoomIds.add(roomId)
      return [] as Array<RoomRateViews>
    }

    const propertyRoom = propertyRooms[roomId]
    const partialRoomRateViews = {
      roomId: propertyRoom.id,
      propertyRoom,
      isSelected: propertyRoom.id === selectedRoomId,
    }

    // TODO(Xiao): Clean up.
    if (isUnavailableRoomRates(roomRates)) {
      if (propertyRoom.shouldHideUnlessHasRates) {
        // NB: We don't expect accommodation service to return rooms flagged as
        // `shouldHideUnlessHasRates` as unavailable, but it doesn't hurt to be defensive.
        return []
      } else if (roomRates.unavailableReason === 'DATE_UNAVAILABLE') {
        return [{
          ...partialRoomRateViews,
          availableRooms: undefined,
          rates: EmptyArray,
          allRatesQuickFiltered: false,
          roomFitsCapacity: false,
          datesAvailable: false,
          isHighlighted: false,
          roomRateUnmatched: true,
        }]
      } else if (roomRates.unavailableReason === 'EXCEEDS_ROOM_CAPACITY') {
        return [{
          ...partialRoomRateViews,
          availableRooms: undefined,
          rates: EmptyArray,
          allRatesQuickFiltered: false,
          roomFitsCapacity: false,
          datesAvailable: true,
          isHighlighted: false,
          roomRateUnmatched: true,
        }]
      } else if (roomRates.unavailableReason === 'QUICK_FILTERED_OUT') {
        return [{
          ...partialRoomRateViews,
          availableRooms: undefined,
          rates: EmptyArray,
          allRatesQuickFiltered: true,
          roomFitsCapacity: false,
          datesAvailable: false,
          isHighlighted: false,
          roomRateUnmatched: true,
        }]
      } else {
        assertUnreachable(roomRates.unavailableReason)
      }
    }

    // TODO(Xiao): Clean up.
    const originalRates = flights ? roomRates.orderedHotelWithFlightsRates : roomRates.orderedHotelOnlyRates
    const rates: Array<App.EnrichedPropertyRate> = originalRates.map(propertyRate => {
      return propertyRate.exceedsIncludedGuests ? {
        ...propertyRate,
        includedGuestsExceededPolicy: propertyRoom.policy,
      } : propertyRate
    })
    const ratesAvailable = !!originalRates.length
    // NB: We don't expect accommodation service to return rooms flagged as
    // `shouldHideUnlessHasRates` with no rates, but it doesn't hurt to be defensive.
    if (!ratesAvailable && propertyRoom.shouldHideUnlessHasRates) return []
    const isHighlighted = roomIndex === 0 && (
      (partialRoomRateViews.propertyRoom === lpcRooms[highlightedRoomId ?? '']) ||
      (partialRoomRateViews.propertyRoom === bedbankRooms[highlightedRoomId ?? ''])
    )
    const roomRateUnmatched =
      !isHighlighted ||
      !originalRates[0] ||
      // Accommodation service will move the highlighted rate to the start of the list for us.
      (originalRates[0].service.rateId !== highlightedRateId)

    return [{
      ...partialRoomRateViews,
      availableRooms: getFirstRateAvailableRooms(rates),
      rates,
      allRatesQuickFiltered: false,
      roomFitsCapacity: ratesAvailable,
      datesAvailable: ratesAvailable,
      isHighlighted,
      roomRateUnmatched,
    }]
  })
  if (droppedRoomIds.size) {
    // Races notwithstanding, this should never happen.
    reportLog('Dropping rates for unexpected accommodation room ID(s)', {
      accommodationPropertyId: mapping.propertyId,
      droppedAccommodationRoomIds: JSON.stringify([...droppedRoomIds].sort()),
    }, 'warn')
  }

  // TODO(HP-1196): Remove workaround when accommodation service reliably returns
  // `unavailableReason`s for all LPC rooms.
  const missingRequiredRoomIds = sortBy(
    Object.entries(defaultOrderByRequiredRoomIds),
    ([_, defaultOrder]) => defaultOrder,
    'asc',
  ).map(([roomId]) => roomId)
  for (const roomId of missingRequiredRoomIds) {
    const propertyRoom = propertyRooms[roomId]
    combinedRatesByRoom.push({
      roomId: propertyRoom.id,
      propertyRoom,
      availableRooms: undefined,
      rates: EmptyArray,
      allRatesQuickFiltered: false,
      roomFitsCapacity: false,
      datesAvailable: false,
      isSelected: propertyRoom.id === selectedRoomId,
      isHighlighted: false,
      roomRateUnmatched: true,
    })
  }

  return combinedRatesByRoom
}

type PayAtPropertyDetails = {
  shouldShowPill: false
} | {
  shouldShowPill: true
  shouldShowModal: false
} | {
  shouldShowPill: true
  shouldShowModal: true
  propertyCollectRate: App.EnrichedPropertyCollectPropertyRate
  leCollectRate: App.EnrichedLeCollectPropertyRate
}

export function getPayAtPropertyDetails(rate: App.EnrichedPropertyRate): PayAtPropertyDetails {
  const paymentMethod = rate.paymentMethod.type
  if (paymentMethod === 'leCollect') {
    invariant(isLeCollectRate(rate))
    if (!rate.relations.paymentMethod.propertyCollect.length) {
      return { shouldShowPill: false }
    }
    return {
      shouldShowPill: true,
      shouldShowModal: true,
      propertyCollectRate: rate.relations.paymentMethod.propertyCollect[0],
      leCollectRate: rate,
    }
  } else if (paymentMethod === 'propertyCollect') {
    invariant(isPropertyCollectRate(rate))
    if (!rate.relations.paymentMethod.leCollect.length) {
      return {
        shouldShowPill: true,
        shouldShowModal: false,
      }
    }
    return {
      shouldShowPill: true,
      shouldShowModal: true,
      propertyCollectRate: rate,
      leCollectRate: rate.relations.paymentMethod.leCollect[0],
    }
  }
  assertUnreachable(paymentMethod)
}
